import { ApiError } from '@/api/client';
import { PWA_PUSH_ACK_KEY } from '@/auth/storageKeys';
import { urlBase64ToUint8Array } from '@/lib/pushEncoding';

export type EnablePushResult =
  | { ok: true }
  | { ok: false; reason: 'insecure' | 'no_sw' | 'denied' | 'incomplete' | 'error'; message: string };

type ApiGet = <T>(path: string) => Promise<T>;
type ApiPost = <T>(path: string, body?: unknown) => Promise<T>;

export type WebPushStatus = 'unsupported' | 'denied' | 'inactive' | 'active';

export type PushServerStatus = {
  configured: boolean;
  serverSubscribed: boolean;
};

function readPushAck(): boolean {
  try {
    return localStorage.getItem(PWA_PUSH_ACK_KEY) === '1';
  } catch {
    return false;
  }
}

function writePushAck(active: boolean): void {
  try {
    if (active) localStorage.setItem(PWA_PUSH_ACK_KEY, '1');
    else localStorage.removeItem(PWA_PUSH_ACK_KEY);
  } catch {
    /* */
  }
}

async function waitForServiceWorker(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const ready = navigator.serviceWorker.ready;
    const timeout = new Promise<null>((resolve) => window.setTimeout(() => resolve(null), timeoutMs));
    const reg = await Promise.race([ready, timeout]);
    return reg;
  } catch {
    return null;
  }
}

async function browserPushSubscription(): Promise<PushSubscription | null> {
  const reg = await waitForServiceWorker();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function getWebPushStatus(server?: PushServerStatus | null): Promise<WebPushStatus> {
  if (!window.isSecureContext || !('Notification' in window) || !('PushManager' in window)) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') {
    writePushAck(false);
    return 'denied';
  }
  if (Notification.permission === 'granted') {
    const sub = await browserPushSubscription();
    if (sub) {
      writePushAck(true);
      return 'active';
    }
    if (readPushAck() || server?.serverSubscribed) return 'active';
  }
  return 'inactive';
}

export async function fetchPushServerStatus(get: ApiGet): Promise<PushServerStatus | null> {
  try {
    return await get<PushServerStatus>('/api/console/push/status');
  } catch {
    return null;
  }
}

export async function enableWebPush(get: ApiGet, post: ApiPost): Promise<EnablePushResult> {
  if (!window.isSecureContext) {
    return {
      ok: false,
      reason: 'insecure',
      message:
        'Este sitio no es un contexto seguro (p. ej. HTTP por IP en la red local). Web Push requiere HTTPS o localhost.',
    };
  }
  if (!('Notification' in window) || !('PushManager' in window)) {
    return { ok: false, reason: 'error', message: 'Este navegador no admite notificaciones push.' };
  }
  try {
    const reg = await waitForServiceWorker();
    if (!reg) {
      return {
        ok: false,
        reason: 'no_sw',
        message: 'El service worker aún no está listo. Cerrá y volvé a abrir la app e intentá de nuevo.',
      };
    }
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      writePushAck(false);
      return { ok: false, reason: 'denied', message: 'Permisos denegados para notificaciones.' };
    }
    const { publicKey } = await get<{ publicKey: string }>('/api/console/push/vapid-public-key');
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: 'incomplete', message: 'Suscripción incompleta.' };
    }
    await post('/api/console/push/subscribe', {
      subscription: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
    });
    writePushAck(true);
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.code === 'NOT_CONFIGURED') {
      return {
        ok: false,
        reason: 'error',
        message:
          'Web Push no está configurado en el servidor (faltan claves VAPID). Pedile al administrador que las active.',
      };
    }
    if (e instanceof DOMException) {
      if (e.name === 'NotAllowedError') {
        writePushAck(false);
        return { ok: false, reason: 'denied', message: 'Permisos denegados para notificaciones.' };
      }
      if (e.name === 'SecurityError') {
        return {
          ok: false,
          reason: 'error',
          message: 'El navegador bloqueó push por política de seguridad (revisá HTTPS y permisos).',
        };
      }
    }
    const message = e instanceof ApiError ? e.message : 'No se pudo activar push.';
    return { ok: false, reason: 'error', message };
  }
}
